/*************************************************************************
 * ChartComponent.jsx
 * Regenerated to accept a company URL parameter and route all endpoints to
 *   https://{company}.ogpumper.com
 *
 * Notes:
 * - Company slug is taken from ?Company= / ?company= / ?Subdomain= / ?c=
 *   or from the path segment after /companyHome/, or from host subdomain.
 * - Fallback default is "stasney".
 * - The HTML/JSX in the return() block is UNCHANGED from your version.
 *************************************************************************/

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { SketchPicker } from "react-color";
import debounce from "lodash/debounce";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRedo,
  faCog,
  faPrint,
  faChartBar,
  faChartLine,
  faTimes,
  faExchangeAlt,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment";

/* -------------------------------------------------------------
   URL-parameter helpers
-------------------------------------------------------------- */
const qs = new URLSearchParams(window.location.search);
const urlRpt     = qs.get("Rpt")     || "CD";
const urlQD      = qs.get("QD")      || "30";
const urlLeaseID = qs.get("LeaseID") || "~ALL~";
const urlFrom    = qs.get("From")    || null;
const urlThru    = qs.get("Thru")    || null;
const urlTag     = qs.get("Tag")     || "All";
const urlUserID  = qs.get("UserID")  || null;

/* -------------------------------------------------------------
   Company detection → API base: https://{company}.ogpumper.com
-------------------------------------------------------------- */
function sanitizeCompany(raw) {
  if (!raw) return "";
  const v = String(raw).trim().toLowerCase();
  return /^[a-z0-9-]+$/.test(v) ? v : "";
}

function detectCompany() {
  // 1) From query
  const qCompany =
    qs.get("Company") ||
    qs.get("company") ||
    qs.get("Subdomain") ||
    qs.get("subdomain") ||
    qs.get("c");
  if (qCompany) {
    const s = sanitizeCompany(qCompany);
    if (s) return s;
  }

  // 2) From path: /companyHome/:slug(/…)
  const parts = window.location.pathname.replace(/^\/+|\/+$/g, "").split("/");
  const idx   = parts.indexOf("companyHome");
  if (idx !== -1 && parts[idx + 1]) {
    const s = sanitizeCompany(parts[idx + 1]);
    if (s) return s;
  }

  // 3) From host subdomain: foo.example.com → foo
  const hostParts = window.location.hostname.split(".");
  if (hostParts.length > 2) {
    const s = sanitizeCompany(hostParts[0]);
    if (s) return s;
  }

  // 4) Fallback default
  return "stasney";
}

const COMPANY  = detectCompany();
const API_BASE = `https://${COMPANY}.ogpumper.com`;

/* -------------------------------------------------------------
   Derive From/Thru dates from QD (mirrors original quick-link logic)
-------------------------------------------------------------- */
const deriveDates = (qd) => {
  const today = moment();
  switch (qd) {
    case "CM":
      return {
        from: today.clone().startOf("month").format("YYYY-MM-DD"),
        thru: today.clone().endOf("month").format("YYYY-MM-DD"),
      };
    case "3D":
      return {
        from: today.clone().subtract(2, "days").format("YYYY-MM-DD"),
        thru: today.clone().format("YYYY-MM-DD"),
      };
    case "7D":
      return {
        from: today.clone().subtract(6, "days").format("YYYY-MM-DD"),
        thru: today.clone().format("YYYY-MM-DD"),
      };
    case "LM":
      return {
        from: today
          .clone()
          .subtract(1, "month")
          .startOf("month")
          .format("YYYY-MM-DD"),
        thru: today
          .clone()
          .subtract(1, "month")
          .endOf("month")
          .format("YYYY-MM-DD"),
      };
    case "3M":
      return {
        from: today.clone().subtract(3, "months").add(1, "day").format("YYYY-MM-DD"),
        thru: today.clone().format("YYYY-MM-DD"),
      };
    case "6M":
      return {
        from: today.clone().subtract(6, "months").add(1, "day").format("YYYY-MM-DD"),
        thru: today.clone().format("YYYY-MM-DD"),
      };
    case "CY":
      return {
        from: today.clone().startOf("year").format("YYYY-MM-DD"),
        thru: today.clone().format("YYYY-MM-DD"),
      };
    case "LY":
      return {
        from: today
          .clone()
          .subtract(1, "year")
          .startOf("year")
          .format("YYYY-MM-DD"),
        thru: today
          .clone()
          .subtract(1, "year")
          .endOf("year")
          .format("YYYY-MM-DD"),
      };
    case "30":
    default:
      return {
        from: today.clone().subtract(29, "days").format("YYYY-MM-DD"),
        thru: today.clone().format("YYYY-MM-DD"),
      };
  }
};

/* -------------------------------------------------------------
   Initial From/Thru (URL overrides derived defaults)
-------------------------------------------------------------- */
const dateDefaults = deriveDates(urlQD);
const initialFrom  = urlFrom || dateDefaults.from;
const initialThru  = urlThru || dateDefaults.thru;

/* -------------------------------------------------------------
   Helper: remove <=0 values when in log mode
-------------------------------------------------------------- */
const filterLogData = (data) =>
  data.map((d) => ({
    ...d,
    TestGas: d.TestGas > 0 ? d.TestGas : null,
    JTF: d.JTF > 0 ? d.JTF : null,
    FTF: d.FTF > 0 ? d.FTF : null,
    FAP: d.FAP > 0 ? d.FAP : null,
    GFFAP: d.GFFAP > 0 ? d.GFFAP : null,
    SND: d.SND > 0 ? d.SND : null,
  }));

/* =============================================================
   MAIN COMPONENT
============================================================= */
const ChartComponent = () => {
  /* -----------------------------------------------------------
     User / URL-param initialisation
  ------------------------------------------------------------ */
  const userID = urlUserID; 

  /* -----------------------------------------------------------
     State
  ------------------------------------------------------------ */
  const [reportType,      setReportType]      = useState(urlRpt);
  const [quickLink,       setQuickLink]       = useState(urlQD);
  const [selectedLeaseID, setSelectedLeaseID] = useState(urlLeaseID);
  const [fromDate,        setFromDate]        = useState(initialFrom);
  const [thruDate,        setThruDate]        = useState(initialThru);
  const [selectedTag,     setSelectedTag]     = useState(urlTag);

  const [selectedWellID, setSelectedWellID] = useState("");
  const [wells,          setWells]          = useState([]);
  const [leases,         setLeases]         = useState([]);
  const [tags,           setTags]           = useState(["All"]);
  const [chartView,      setChartView]      = useState("production");

  const chartRef   = useRef(null);
  const [data, setData]   = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  /* ---- chartTypes, colours, prefs, etc. (unchanged) ---------- */
  const [chartTypes, setChartTypes] = useState({
    Oil: "bar",
    ProducedWater: "bar",
    InjectedWater: "bar",
    Gas: "line",
    Tbg: "line",
    Csg: "line",
    TestOil: "bar",
    TestWater: "bar",
    TestGas: "line",
    JTF: "line",
    FTF: "line",
    FAP: "line",
    GFFAP: "line",
    SND: "line",
  });
  const [colors, setColors] = useState({
    Oil: "#FF7F0E",
    ProducedWater: "#2CA02C",
    InjectedWater: "#1F77B4",
    Tbg: "#D62728",
    Csg: "#9467BD",
    Gas: "#8C564B",
    TestOil: "#FF7F0E",
    TestWater: "#2CA02C",
    TestGas: "#8C564B",
    JTF: "#FFBB78",
    FTF: "#98DF8A",
    FAP: "#AEC7E8",
    GFFAP: "#FF9896",
    SND: "#C5B0D5",
  });
  const [colorPicker, setColorPicker] = useState({ visible: false, field: null });
  const [logarithmic, setLogarithmic] = useState(false);
  const [stacked, setStacked]         = useState(false);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [isEditing, setIsEditing]             = useState(false);
  const [disabledSeries, setDisabledSeries]   = useState([]);

  /* -----------------------------------------------------------
     Sync state → URL bar (preserve current company & params)
  ------------------------------------------------------------ */
  useEffect(() => {
    const p = new URLSearchParams();
    p.set("Rpt",      reportType);
    p.set("QD",       quickLink);
    p.set("LeaseID",  selectedLeaseID);
    p.set("From",     fromDate);
    p.set("Thru",     thruDate);
    p.set("Tag",      selectedTag);
    if (userID)  p.set("UserID", userID);
    // Keep company visible for shareable URLs
    p.set("Company", COMPANY);
    window.history.replaceState(null, "", `${window.location.pathname}?${p.toString()}`);
  }, [reportType, quickLink, selectedLeaseID, fromDate, thruDate, selectedTag, userID]);

  /* -----------------------------------------------------------
     Fetch user prefs
  ------------------------------------------------------------ */
  const fetchPreferences = useCallback(async () => {
    try {
      const resp = await fetch(`${API_BASE}/api/userdetails.php?id=${userID}&chartsPref=true`);
      const json = await resp.json();
      if (json.success && json.ChartsPref) {
        const { chartTypes, colors, logarithmic, disabledSeries } = json.ChartsPref;
        setChartTypes({ ...chartTypes });
        setColors({ ...colors });
        setLogarithmic(!!logarithmic);
        setDisabledSeries(disabledSeries || []);
      }
    } catch (e) {
      console.error("fetchPreferences:", e);
    }
  }, [userID]);

  /* -----------------------------------------------------------
     Fetch leases + tags
  ------------------------------------------------------------ */
  const fetchLeases = useCallback(async () => {
    try {
      const resp = await fetch(`${API_BASE}/api/leases.php`);
      if (!resp.ok) throw new Error("Network response was not ok");
      const leaseData = await resp.json();

      setLeases(leaseData);
      setWells(
        leaseData.flatMap((lease) =>
          lease.Wells.map((w) => ({ ...w, LeaseName: lease.LeaseName }))
        )
      );
      const uniqueTags = [
        ...new Set(
          leaseData.flatMap((l) => [l.Tag1, l.Tag2, l.Tag3, l.Tag4].filter(Boolean))
        ),
      ];
      setTags(["All", ...uniqueTags]);
    } catch (e) {
      console.error("fetchLeases:", e);
    }
  }, []);

  /* -----------------------------------------------------------
     Fetch production / well-test data
  ------------------------------------------------------------ */
  const fetchChartData = useCallback(async () => {
    try {
      const resp = await fetch(
        `${API_BASE}/service_testprod.php?Rpt=${reportType}` +
          `&QD=${quickLink}` +
          `&LeaseID=${encodeURIComponent(selectedLeaseID)}` +
          `&From=${fromDate}&Thru=${thruDate}&Tag=${selectedTag}`
      );
      if (!resp.ok) throw new Error("Network response was not ok");
      const json = await resp.json();
      return json.map((i) => ({
        GaugeDate: reportType === "CM" ? i.MonthYear : moment(i.GaugeDate).format("YYYY-MM-DD"),
        Oil:            parseFloat(i.Produced)             || 0,
        ProducedWater:  parseFloat(i.ProducedWaterTotal)   || 0,
        InjectedWater:  parseFloat(i.InjectedWaterTotal)   || 0,
        Gas:            parseFloat(i.Gas)                  || 0,
        Tbg:            parseFloat(i.Tbg)                  || 0,
        Csg:            parseFloat(i.Csg)                  || 0,
      }));
    } catch (e) {
      console.error("fetchChartData:", e);
      return [];
    }
  }, [reportType, quickLink, selectedLeaseID, fromDate, thruDate, selectedTag]);

  const fetchWellTestData = useCallback(async () => {
    try {
      const resp = await fetch(
        `${API_BASE}/service_welltests.php?LeaseID=${selectedLeaseID}` +
          `&WellID=${selectedWellID}&FromDate=${fromDate}&ThruDate=${thruDate}`
      );
      if (!resp.ok) throw new Error("Network response was not ok");
      const json = await resp.json();
      return json.map((i) => ({
        GaugeDate: i.GaugeDate,
        TestOil:   parseFloat(i.TestOil)   || 0,
        TestWater: parseFloat(i.TestWater) || 0,
        TestGas:   parseFloat(i.TestGas)   || 0,
        Tbg:       parseFloat(i.TbgPressure) || 0,
        Csg:       parseFloat(i.CsgPressure) || 0,
        JTF:       parseFloat(i.JTF) || 0,
        FTF:       parseFloat(i.FTF) || 0,
        FAP:       parseFloat(i.FAP) || 0,
        GFFAP:     parseFloat(i.GFFAP) || 0,
        SND:       parseFloat(i.SNDepth) || 0,
      }));
    } catch (e) {
      console.error("fetchWellTestData:", e);
      return [];
    }
  }, [selectedLeaseID, selectedWellID, fromDate, thruDate]);

  /* -----------------------------------------------------------
     On-mount fetches
  ------------------------------------------------------------ */
  useEffect(() => {
    fetchPreferences();
    fetchLeases();
  }, [fetchPreferences, fetchLeases]);

  /* -----------------------------------------------------------
     Fetch data when dependencies change
  ------------------------------------------------------------ */
  useEffect(() => {
    let mounted = true;
    (async () => {
      setIsLoading(true);
      try {
        const result =
          chartView === "production" ? await fetchChartData() : await fetchWellTestData();
        if (mounted) {
          setData(result);
          setIsLoading(false);
        }
      } catch (e) {
        console.error("fetchData:", e);
        if (mounted) setIsLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [chartView, fetchChartData, fetchWellTestData]);

  /* -----------------------------------------------------------
     Debounced preference saver
  ------------------------------------------------------------ */
  const debouncedSavePreferences = useCallback(
    debounce(async () => {
      try {
        const resp = await fetch(`${API_BASE}/api/userdetails.php`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            UserID: userID,
            ChartsPref: { chartTypes, colors, logarithmic, disabledSeries },
          }),
        });
        const json = await resp.json();
        if (!json.success) console.error("savePrefs:", json.message);
      } catch (e) {
        console.error("savePrefs:", e);
      }
    }, 300),
    [userID, chartTypes, colors, logarithmic, disabledSeries]
  );

  useEffect(() => {
    debouncedSavePreferences();
  }, [chartTypes, colors, logarithmic, disabledSeries, debouncedSavePreferences]);

  /* -----------------------------------------------------------
     QuickLink handler (updates dates & state)
  ------------------------------------------------------------ */
  const handleQuickLinkChange = (qd) => {
    setQuickLink(qd);
    const { from, thru } = deriveDates(qd);
    setFromDate(from);
    setThruDate(thru);
  };

  /* -----------------------------------------------------------
     Remaining handlers (toggle, legend, colour, navigate, etc.)
     — identical to your original implementation.
  ------------------------------------------------------------ */
  const handleToggle = (field) =>
    setChartTypes((p) => ({ ...p, [field]: p[field] === "line" ? "bar" : "line" }));

  const handleColorChange = (c) => {
    setColors((p) => ({ ...p, [colorPicker.field]: c.hex }));
    setColorPicker({ visible: false, field: null });
  };

  const handleLegendClick = ({ dataKey }) =>
    setDisabledSeries((p) =>
      p.includes(dataKey) ? p.filter((i) => i !== dataKey) : [...p, dataKey]
    );

  const resetLegend = () => setDisabledSeries([]);

  const renderLegendText = (v, entry) => {
    const { color } = entry;
    let unit = "BBLS";
    if (v === "Gas" || v === "TestGas" || v === "JTF" || v === "FTF" || v === "FAP" || v === "GFFAP" || v === "SND")
      unit = "MCF";
    return (
      <span style={{ color: disabledSeries.includes(v) ? "#ccc" : color }}>
        {v} ({unit})
      </span>
    );
  };

  useEffect(() => {
    if (selectedLeaseID !== "~ALL~") {
      const lease = leases.find((l) => l.LeaseID === selectedLeaseID);
      if (lease) {
        setWells(lease.Wells);
        setSelectedWellID(lease.Wells.length ? lease.Wells[0].WellID : "");
      }
    } else {
      setWells(
        leases.flatMap((l) => l.Wells.map((w) => ({ ...w, LeaseName: l.LeaseName })))
      );
      setSelectedWellID("");
    }
  }, [selectedLeaseID, leases]);

  const handleNextLease = () => {
    const idx = leases.findIndex((l) => l.LeaseID === selectedLeaseID);
    const next = (idx + 1) % leases.length;
    setSelectedLeaseID(leases[next].LeaseID);
  };

  const handleNextWell = () => {
    const idx = wells.findIndex((w) => w.WellID === selectedWellID);
    const next = (idx + 1) % wells.length;
    setSelectedWellID(wells[next].WellID);
  };

  const handlePrint = () => {
    const html = chartRef.current.innerHTML;
    const original = document.body.innerHTML;
    const leaseName =
      leases.find((l) => l.LeaseID === selectedLeaseID)?.LeaseName || "All Leases";
    const fmt = (d) =>
      `${new Date(d).getMonth() + 1}/${new Date(d).getDate()}/${new Date(d)
        .getFullYear()
        .toString()
        .slice(-2)}`;

    document.body.innerHTML = `
      <style>@media print{@page{size:landscape;margin:.5cm;}body{margin:0;font-family:Arial}}</style>
      <h1>${chartView === "production" ? "Production" : "Well Test"} Chart</h1>
      <p><b>Date Range:</b> ${fmt(fromDate)} to ${fmt(thruDate)}</p>
      <p><b>Lease:</b> ${leaseName}</p>
      ${
        chartView === "production" && selectedTag !== "All"
          ? `<p><b>Tag:</b> ${selectedTag}</p>`
          : ""
      }
      ${
        chartView === "wellTest"
          ? `<p><b>Well:</b> ${selectedWellID || "All Wells"}</p>`
          : ""
      }
      <p><b>Y-Axis:</b> ${logarithmic ? "Logarithmic" : "Linear"}</p>
      ${html}
    `;
    window.print();
    document.body.innerHTML = original;
    window.location.reload();
  };

  const handleEditClick = () => {
    setIsSidePanelOpen((o) => !o);
    setIsEditing((o) => !o);
  };

  const handleChartViewChange = () => {
    setChartView((v) => (v === "production" ? "wellTest" : "production"));
    setData([]);
  };

  /* -----------------------------------------------------------
   * HTML / JSX SEGMENT                                (UNTOUCHED)
   * *Everything within the `return (…)` block below is **exactly**
   *  as you provided earlier.  No lines changed, added, or removed.*
   ------------------------------------------------------------ */
  return (
    <>
      <div className="chart-container">
        <div className={`filters-container ${isEditing ? "editing" : ""}`}>
          <div className="filters">
            <div className="filter-group">
              <div className="filter-item">
                <label>Time Range</label>
                <select
                  value={quickLink}
                  onChange={(e) => handleQuickLinkChange(e.target.value)}
                >
                  <option value="30">Last 30 days</option>
                  <option value="CM">Current Month</option>
                  <option value="3D">Last 3 Days</option>
                  <option value="7D">Last 7 Days</option>
                  <option value="LM">Last Month</option>
                  <option value="3M">Last 3 months</option>
                  <option value="6M">Last 6 months</option>
                  <option value="CY">Current Year</option>
                  <option value="LY">Last Year</option>
                </select>
              </div>
              <div className="filter-item">
                <label>From</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>
              <div className="filter-item">
                <label>To</label>
                <input
                  type="date"
                  value={thruDate}
                  onChange={(e) => setThruDate(e.target.value)}
                />
              </div>

              <div className="filter-item">
                <label>Lease</label>
                <select
                  value={selectedLeaseID}
                  onChange={(e) => setSelectedLeaseID(e.target.value)}
                >
                  {chartView === "production" ? (
                    <option value="~ALL~">All Leases</option>
                  ) : (
                    leases.length > 0 && (
                      <option value={leases[0].LeaseID} key={leases[0].LeaseID}>
                        {leases[0].LeaseName}
                      </option>
                    )
                  )}
                  {leases
                    .filter(
                      (lease) =>
                        chartView === "production" || lease.WellType !== "INJ"
                    )
                    .map((lease, index) => (
                      <option
                        key={lease.LeaseID}
                        value={lease.LeaseID}
                        selected={chartView !== "production" && index === 0}
                      >
                        {lease.LeaseName}
                      </option>
                    ))}
                </select>
                <button className="next-well-button" onClick={handleNextLease}>
                  <FontAwesomeIcon icon={faArrowRight} />
                </button>
              </div>
              {chartView === "production" ? (
                <div className="filter-item">
                  <label>Tag</label>
                  <select
                    value={selectedTag}
                    onChange={(e) => setSelectedTag(e.target.value)}
                  >
                    {tags.map((tag) => (
                      <option key={tag} value={tag}>
                        {tag}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="filter-item">
                  <label>Well</label>
                  <select
                    value={selectedWellID}
                    onChange={(e) => setSelectedWellID(e.target.value)}
                    disabled={selectedLeaseID === "~ALL~"}
                  >
                    <option value="">Select a well</option>
                    {wells.map((well) => (
                      <option key={well.UniqID} value={well.WellID}>
                        {well.WellID}
                      </option>
                    ))}
                  </select>
                  <button className="next-well-button" onClick={handleNextWell}>
                    <FontAwesomeIcon icon={faArrowRight} />
                  </button>
                </div>
              )}
              {chartView === "production" && (
                <div className="filter-item">
                  <label>Report Type</label>
                  <select
                    value={reportType}
                    onChange={(e) => setReportType(e.target.value)}
                  >
                    <option value="CD">Daily</option>
                    <option value="CM">Monthly</option>
                  </select>
                </div>
              )}
            </div>
          </div>
          <div className="action-buttons">
            <button
              className="action-button print-button"
              onClick={handlePrint}
            >
              <FontAwesomeIcon icon={faPrint} className="icon" /> Print
            </button>
            <button
              className="action-button settings-button"
              onClick={handleEditClick}
            >
              <FontAwesomeIcon
                icon={isSidePanelOpen ? faTimes : faCog}
                className="icon"
              />
              {isSidePanelOpen ? "Done" : "Edit"}
            </button>
          </div>
        </div>
        <div ref={chartRef} className="chart-container-inner">
          {isLoading ? (
            <div className="loading-spinner"></div>
          ) : (
            <ResponsiveContainer width="100%" height={700} key={chartView}>
              <ComposedChart data={logarithmic ? filterLogData(data) : data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="GaugeDate"
                  tickFormatter={(t) =>
                    reportType === "CM"
                      ? moment(t, "MM-YYYY").format("M/YY")
                      : moment(t, "YYYY-MM-DD").format("M/D/YY")
                  }
                  interval="preserveStartEnd"
                  tick={{ fontSize: 12, angle: -45, textAnchor: "end" }}
                  height={60}
                  padding={{ left: 20, right: 20 }}
                />
                <YAxis
                  yAxisId="left"
                  scale={logarithmic ? "log" : "linear"}
                  domain={logarithmic ? [1, "auto"] : [0, (max) => max * 1.1]}
                  allowDataOverflow
                  label={{ value: "BBLS", angle: -90, position: "insideLeft" }}
                  tickCount={logarithmic ? undefined : 7}
                  tickFormatter={(v) =>
                    logarithmic
                      ? v >= 1e6
                        ? `${(v / 1e6).toFixed(1)}M`
                        : v >= 1e3
                        ? `${(v / 1e3).toFixed(1)}K`
                        : v.toFixed(1)
                      : v >= 1e6
                      ? `${(v / 1e6).toFixed(1)}M`
                      : v >= 1e3
                      ? `${(v / 1e3).toFixed(1)}K`
                      : v.toFixed(1)
                  }
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  scale={logarithmic ? "log" : "linear"}
                  domain={logarithmic ? [1, "auto"] : [0, (max) => max * 1.1]}
                  allowDataOverflow
                  label={{ value: "MCF", angle: -90, position: "insideRight" }}
                  tickCount={logarithmic ? undefined : 7}
                  tickFormatter={(v) =>
                    logarithmic
                      ? v >= 1e6
                        ? `${(v / 1e6).toFixed(1)}M`
                        : v >= 1e3
                        ? `${(v / 1e3).toFixed(1)}K`
                        : v.toFixed(1)
                      : v >= 1e6
                      ? `${(v / 1e6).toFixed(1)}M`
                      : v >= 1e3
                      ? `${(v / 1e3).toFixed(1)}K`
                      : v.toFixed(1)
                  }
                />
                <Tooltip />
                <Legend onClick={handleLegendClick} formatter={renderLegendText} />
                {/* === SERIES RENDERING (unchanged) ================== */}
                {chartView === "production" ? (
                  <>
                    {!disabledSeries.includes("Oil") &&
                      (chartTypes.Oil === "bar" ? (
                        <Bar yAxisId="left" dataKey="Oil" fill={colors.Oil} />
                      ) : (
                        <Line
                          yAxisId="left"
                          type="monotone"
                          dataKey="Oil"
                          stroke={colors.Oil}
                          dot={false}
                        />
                      ))}
                    {!disabledSeries.includes("ProducedWater") &&
                      (chartTypes.ProducedWater === "bar" ? (
                        <Bar
                          yAxisId="left"
                          dataKey="ProducedWater"
                          fill={colors.ProducedWater}
                        />
                      ) : (
                        <Line
                          yAxisId="left"
                          type="monotone"
                          dataKey="ProducedWater"
                          stroke={colors.ProducedWater}
                          dot={false}
                        />
                      ))}
                    {!disabledSeries.includes("InjectedWater") &&
                      (chartTypes.InjectedWater === "bar" ? (
                        <Bar
                          yAxisId="left"
                          dataKey="InjectedWater"
                          fill={colors.InjectedWater}
                        />
                      ) : (
                        <Line
                          yAxisId="left"
                          type="monotone"
                          dataKey="InjectedWater"
                          stroke={colors.InjectedWater}
                          dot={false}
                        />
                      ))}
                    {!disabledSeries.includes("Gas") &&
                      (chartTypes.Gas === "bar" ? (
                        <Bar yAxisId="right" dataKey="Gas" fill={colors.Gas} />
                      ) : (
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="Gas"
                          stroke={colors.Gas}
                          dot={false}
                        />
                      ))}
                    {!disabledSeries.includes("Tbg") &&
                      (chartTypes.Tbg === "bar" ? (
                        <Bar yAxisId="right" dataKey="Tbg" fill={colors.Tbg} />
                      ) : (
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="Tbg"
                          stroke={colors.Tbg}
                          dot={false}
                        />
                      ))}
                    {!disabledSeries.includes("Csg") &&
                      (chartTypes.Csg === "bar" ? (
                        <Bar yAxisId="right" dataKey="Csg" fill={colors.Csg} />
                      ) : (
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="Csg"
                          stroke={colors.Csg}
                          dot={false}
                        />
                      ))}
                  </>
                ) : (
                  <>
                    {!disabledSeries.includes("TestOil") &&
                      (chartTypes.TestOil === "bar" ? (
                        <Bar
                          yAxisId="left"
                          dataKey="TestOil"
                          name="Test Oil"
                          fill={colors.TestOil}
                        />
                      ) : (
                        <Line
                          yAxisId="left"
                          type="monotone"
                          dataKey="TestOil"
                          name="Test Oil"
                          stroke={colors.TestOil}
                          dot={false}
                        />
                      ))}
                    {!disabledSeries.includes("TestWater") &&
                      (chartTypes.TestWater === "bar" ? (
                        <Bar
                          yAxisId="left"
                          dataKey="TestWater"
                          name="Test Water"
                          fill={colors.TestWater}
                        />
                      ) : (
                        <Line
                          yAxisId="left"
                          type="monotone"
                          dataKey="TestWater"
                          name="Test Water"
                          stroke={colors.TestWater}
                          dot={false}
                        />
                      ))}
                    {!disabledSeries.includes("TestGas") &&
                      (chartTypes.TestGas === "bar" ? (
                        <Bar
                          yAxisId="right"
                          dataKey="TestGas"
                          name="Test Gas"
                          fill={colors.TestGas}
                        />
                      ) : (
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="TestGas"
                          name="Test Gas"
                          stroke={colors.TestGas}
                          dot={false}
                        />
                      ))}
                    {!disabledSeries.includes("Tbg") &&
                      (chartTypes.Tbg === "bar" ? (
                        <Bar yAxisId="right" dataKey="Tbg" fill={colors.Tbg} />
                      ) : (
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="Tbg"
                          stroke={colors.Tbg}
                          dot={false}
                        />
                      ))}
                    {!disabledSeries.includes("Csg") &&
                      (chartTypes.Csg === "bar" ? (
                        <Bar yAxisId="right" dataKey="Csg" fill={colors.Csg} />
                      ) : (
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="Csg"
                          stroke={colors.Csg}
                          dot={false}
                        />
                      ))}
                    {!disabledSeries.includes("JTF") &&
                      (chartTypes.JTF === "bar" ? (
                        <Bar yAxisId="right" dataKey="JTF" name="JTF" fill={colors.JTF} />
                      ) : (
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="JTF"
                          name="JTF"
                          stroke={colors.JTF}
                          dot={false}
                        />
                      ))}
                    {!disabledSeries.includes("FTF") &&
                      (chartTypes.FTF === "bar" ? (
                        <Bar yAxisId="right" dataKey="FTF" name="FTF" fill={colors.FTF} />
                      ) : (
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="FTF"
                          name="FTF"
                          stroke={colors.FTF}
                          dot={false}
                        />
                      ))}
                    {!disabledSeries.includes("FAP") &&
                      (chartTypes.FAP === "bar" ? (
                        <Bar yAxisId="right" dataKey="FAP" name="FAP" fill={colors.FAP} />
                      ) : (
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="FAP"
                          name="FAP"
                          stroke={colors.FAP}
                          dot={false}
                        />
                      ))}
                    {!disabledSeries.includes("GFFAP") &&
                      (chartTypes.GFFAP === "bar" ? (
                        <Bar yAxisId="right" dataKey="GFFAP" name="GFFAP" fill={colors.GFFAP} />
                      ) : (
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="GFFAP"
                          name="GFFAP"
                          stroke={colors.GFFAP}
                          dot={false}
                        />
                      ))}
                    {!disabledSeries.includes("SND") &&
                      (chartTypes.SND === "bar" ? (
                        <Bar yAxisId="right" dataKey="SND" name="SND" fill={colors.SND} />
                      ) : (
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="SND"
                          name="SND"
                          stroke={colors.SND}
                          dot={false}
                        />
                      ))}
                  </>
                )}
              </ComposedChart>
            </ResponsiveContainer>
          )}
          <button className="reset-legend-button" onClick={resetLegend}>
            <FontAwesomeIcon icon={faRedo} className="icon" /> Reset Legend
          </button>
        </div>
        <div className={`settings-panel ${isSidePanelOpen ? "open" : ""}`}>
          <h2>Settings</h2>
          <div className="settings-section">
            <h3>Chart Type and Color</h3>
            {Object.entries(
              chartView === "production"
                ? {
                    Oil: chartTypes.Oil,
                    ProducedWater: chartTypes.ProducedWater,
                    InjectedWater: chartTypes.InjectedWater,
                    Gas: chartTypes.Gas,
                    Tbg: chartTypes.Tbg,
                    Csg: chartTypes.Csg,
                  }
                : {
                    TestOil: chartTypes.TestOil,
                    TestWater: chartTypes.TestWater,
                    TestGas: chartTypes.TestGas,
                    Tbg: chartTypes.Tbg,
                    Csg: chartTypes.Csg,
                    JTF: chartTypes.JTF,
                    FTF: chartTypes.FTF,
                    FAP: chartTypes.FAP,
                    GFFAP: chartTypes.GFFAP,
                    SND: chartTypes.SND,
                  }
            ).map(([field, type]) => (
              <div key={field} className="settings-item">
                <span>{field}</span>
                <div className="chart-controls">
                  <button
                    className="chart-type-button"
                    onClick={() => handleToggle(field)}
                  >
                    {type === "line" ? (
                      <FontAwesomeIcon icon={faChartBar} />
                    ) : (
                      <FontAwesomeIcon icon={faChartLine} />
                    )}
                  </button>
                  <div
                    className="color-box"
                    style={{ backgroundColor: colors[field] }}
                    onClick={() => setColorPicker({ visible: true, field })}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="settings-section">
            <h3>Y-Axis Scale</h3>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={logarithmic}
                onChange={() => setLogarithmic(!logarithmic)}
              />
              Logarithmic
            </label>
          </div>
          {chartView === "production" && (
            <div className="settings-section">
              <h3>Stacked Options</h3>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={stacked}
                  onChange={() => setStacked(!stacked)}
                />
                Stacked Gas and Tbg
              </label>
            </div>
          )}
        </div>
        {colorPicker.visible && (
          <div className="color-picker-modal">
            <div className="color-picker-container">
              <SketchPicker
                color={colors[colorPicker.field]}
                onChangeComplete={handleColorChange}
              />
              <button
                className="close-button"
                onClick={() => setColorPicker({ visible: false, field: null })}
              >
                Close
              </button>
            </div>
          </div>
        )}
        <div className="fab-container">
          <button className="fab" onClick={handleChartViewChange}>
            <FontAwesomeIcon icon={faExchangeAlt} />
            {chartView === "production" ? "Wells" : "Prod"}
          </button>
        </div>
      </div>

      {/* ======= STYLE BLOCK (unchanged) ======= */}
      <style jsx>{`
        .chart-container {
          min-height: 100vh;
          padding: 16px;
          display: flex;
          flex-direction: column;
          align-items: center;
          margin-top: 0px;
          width: 100%;
          box-sizing: border-box;
        }

        .filters-container {
          width: 100%;
          max-width: 80%;
          background: linear-gradient(to right, #ebf4ff, #dee6ff);
          border-radius: 8px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          padding: 8px;
          display: flex;
          align-items: center;
          transition: transform 0.3s ease-in-out;
        }

        .filters-container.editing {
          transform: translateX(-100px);
        }

        .filters {
          flex: 1;
          display: flex;
          flex-wrap: wrap;
          align-items: center;
        }

        .filter-group {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
          flex: 1;
        }

        .filter-item {
          display: flex;
          flex-direction: column;
          margin-bottom: 0;
        }

        .filter-item label {
          margin-bottom: 4px;
          font-size: 12px;
          font-weight: 500;
          color: #4a4a4a;
        }

        .filter-item select,
        .filter-item input {
          width: 100%;
          max-width: 200px;
          padding: 4px 8px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }

        .filter-item select:focus,
        .filter-item input:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3);
          outline: none;
        }

        .action-buttons {
          display: flex;
          gap: 8px;
          margin-left: 16px;
        }

        .action-button {
          padding: 8px 12px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 600;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background-color 0.15s ease, box-shadow 0.15s ease;
        }

        .print-button {
          background-color: #10b981;
          color: #fff;
          box-shadow: 0 2px 4px rgba(16, 185, 129, 0.2);
        }

        .print-button:hover {
          background-color: #059669;
        }

        .settings-button {
          background-color: #3b82f6;
          color: #fff;
          box-shadow: 0 2px 4px rgba(59, 130, 246, 0.2);
        }

        .settings-button:hover {
          background-color: #2563eb;
        }

        .next-lease-button,
        .next-well-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 4px;
          margin-left: 10px;
          background-color: transparent;
          color: #1d72b8;
          border: none;
          cursor: pointer;
          transition: color 0.3s ease;
        }

        .next-lease-button:hover,
        .next-well-button:hover {
          color: #155a8a;
        }

        .next-lease-button:focus,
        .next-well-button:focus {
          outline: none;
          box-shadow: 0 0 0 2px rgba(29, 114, 184, 0.4);
        }

        .next-lease-button .fa-icon,
        .next-well-button .fa-icon {
          margin-left: 0;
        }

        .chart-container-inner {
          width: 100%;
          max-width: 100%;
          background-color: #ffffff;
          border-radius: 8px;
          padding: 32px;
          margin-bottom: 32px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }

        .loading-spinner {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 400px;
        }

        .loading-spinner::after {
          content: "";
          display: block;
          width: 64px;
          height: 64px;
          border: 8px solid #f3f4f6;
          border-top: 8px solid #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .reset-legend-button {
          margin-top: 16px;
          padding: 8px 16px;
          background-color: #3b82f6;
          color: #fff;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: background-color 0.15s ease, box-shadow 0.15s ease;
        }

        .reset-legend-button:hover { background-color: #2563eb; }

        .settings-panel {
          position: fixed;
          right: -280px;
          top: 10%;
          height: auto;
          max-height: 80vh;
          overflow-y: auto;
          width: 280px;
          padding: 24px;
          background-color: #ffffff;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          transition: right 0.3s ease-in-out;
        }

        .settings-panel.open { right: 0; }

        .settings-panel h2 { font-size: 18px; font-weight: 700; margin-bottom: 16px; }
        .settings-section { margin-bottom: 24px; }
        .settings-section h3 { font-size: 16px; font-weight: 600; margin-bottom: 8px; }
        .settings-item { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }

        .chart-controls { display: flex; align-items: center; gap: 8px; }

        .chart-type-button {
          padding: 6px;
          border-radius: 4px;
          background-color: #3b82f6;
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background-color 0.15s ease, box-shadow 0.15s ease;
        }
        .chart-type-button:hover { background-color: #2563eb; }

        .color-box {
          width: 24px;
          height: 24px;
          border-radius: 4px;
          cursor: pointer;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
        }

        .checkbox-label { display: flex; align-items: center; }
        .checkbox-label input { margin-right: 8px; }

        .color-picker-modal {
          position: fixed;
          top: 0; left: 0; width: 100%; height: 100%;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex; align-items: center; justify-content: center;
        }

        .color-picker-container {
          background-color: #ffffff;
          padding: 24px;
          border-radius: 8px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }

        .close-button {
          margin-top: 16px;
          padding: 8px 16px;
          background-color: #ef4444;
          color: #fff;
          border-radius: 4px;
          display: flex; align-items: center; justify-content: center;
          font-size: 14px; font-weight: 600; cursor: pointer;
          transition: background-color 0.15s ease, box-shadow 0.15s ease;
        }
        .close-button:hover { background-color: #dc2626; }

        .fab-container { position: fixed; bottom: 32px; right: 32px; z-index: 1000; }
        .fab {
          width: 56px; height: 56px; border-radius: 28px; background-color: #3b82f6; color: white;
          display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 5px rgba(0,0,0,0.2);
          border: none; cursor: pointer; transition: all 0.3s ease;
        }
        .fab:hover { background-color: #2563eb; box-shadow: 0 4px 8px rgba(0,0,0,0.3); }
      `}</style>
    </>
  );
};

export default ChartComponent;
